import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, Bell, MessageSquareHeart } from "lucide-react";
import LandingNav from "@/components/LandingNav";
import ProblemStatement from "@/components/ProblemStatement";
import StoryCards from "@/components/StoryCards";
import FAQSection from "@/components/FAQSection";
import SEOHead from "@/components/SEOHead";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

const steps = [
  {
    num: 1,
    icon: UserPlus,
    title: "Add the people who matter.",
    desc: "Add a contact, their birthday, and any important dates in their life. Anniversaries, new babies, graduations — the moments worth remembering. Every person you add is a quiet commitment to show up for them.",
  },
  {
    num: 2,
    icon: Bell,
    title: "Tell Circle who they are.",
    desc: "Add notes about their personality, what they love, shared memories between you. Circle holds onto the details that make every message feel personal. The details you share make every message feel like it came from someone who truly knows them. Because it did.",
  },
  {
    num: 3,
    icon: MessageSquareHeart,
    title: "Show up, every time.",
    desc: "Circle reminds you ahead of every important date. Write a message that sounds like you in seconds. Send via WhatsApp or SMS. Over time, the people you love stop wondering if you care. They know.",
  },
];

const Index = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  // Only apply parallax on desktop
  const heroTextY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, prefersReducedMotion || isMobile ? 0 : 80]
  );

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  // Animation helpers
  const fadeUp = prefersReducedMotion
    ? {}
    : { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  const staggerContainer = prefersReducedMotion
    ? {}
    : { initial: "hidden", whileInView: "visible", viewport: { once: true } };

  const staggerChildren = (stagger = 0.1) =>
    prefersReducedMotion
      ? {}
      : {
          variants: {
            hidden: {},
            visible: { transition: { staggerChildren: stagger } },
          },
        };

  const staggerItem = prefersReducedMotion
    ? {}
    : {
        variants: {
          hidden: { opacity: 0, y: 20 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
        },
      };

  const buttonHover = prefersReducedMotion ? {} : { whileHover: { scale: 1.05 }, transition: { type: "spring" as const, stiffness: 300 } };

  return (
    <div className="flex flex-col bg-background">
      <SEOHead />
      <LandingNav />

      <main>

      {/* Hero with video background */}
      <section
        ref={heroRef}
        className="relative flex flex-col items-center justify-center overflow-hidden px-6 pt-20 pb-20 md:pt-28 md:pb-28"
        style={{ minHeight: "90vh" }}
      >
        {/* Video background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute object-cover object-center"
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            minWidth: "100%",
            minHeight: "100%",
            width: "auto",
            height: "auto",
          }}
          poster="/hero-video-poster.jpg"
        >
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>
        {/* Cream overlay */}
        <div className="absolute inset-0" style={{ background: "rgba(237, 233, 225, 0.65)" }} />

        <motion.div
          className="relative z-10 mx-auto max-w-xl text-center"
          style={{ y: isMobile ? 0 : heroTextY }}
          {...staggerContainer}
          {...staggerChildren(0.15)}
        >
          <motion.p
            className="mb-3 font-serif text-lg italic text-accent"
            {...staggerItem}
          >
            The people you love deserve to feel remembered.
          </motion.p>
          <motion.h1
            className="mb-6 font-serif text-5xl font-semibold leading-tight tracking-tight text-foreground md:text-6xl"
            {...staggerItem}
          >
            Your relationship memory.
          </motion.h1>
          <motion.p
            className="mb-10 text-lg leading-relaxed text-muted-foreground md:text-xl"
            {...staggerItem}
          >
            Because good intentions don't send birthday messages. Circle does.
          </motion.p>
          <motion.div {...staggerItem}>
            <motion.div {...buttonHover} className="inline-block">
              <Button
                asChild
                className="rounded-full px-8 py-6 text-base font-medium"
                size="lg"
              >
                <Link to="/signup">Start for free →</Link>
              </Button>
            </motion.div>
            <p className="mt-4 text-sm text-muted-foreground">
              No credit card required.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Problem Statement */}
      <ProblemStatement />

      {/* How It Works */}
      <section id="how-it-works" className="bg-background px-6 py-20 md:py-28">
        <div className="mx-auto max-w-4xl text-center">
          <motion.span
            className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent"
            {...fadeUp}
            transition={{ duration: 0.5 }}
          >
            How It Works
          </motion.span>
          <motion.h2
            className="mb-16 font-serif text-3xl font-semibold text-foreground md:text-4xl"
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Simple to start. Irreplaceable over time.
          </motion.h2>
          <motion.div
            className="grid gap-12 md:grid-cols-3 md:gap-8"
            {...staggerContainer}
            {...staggerChildren(0.1)}
          >
            {steps.map((s) => (
              <motion.div key={s.num} className="flex flex-col items-center" {...staggerItem}>
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {s.num}
                </div>
                <s.icon className="mb-4 h-7 w-7 text-accent" strokeWidth={1.5} />
                <h3 className="mb-2 font-serif text-lg font-semibold text-foreground">{s.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story Cards */}
      <StoryCards />

      {/* FAQ */}
      <FAQSection />

      {/* Bottom CTA */}
      <motion.section
        className="px-6 py-20 md:py-28 bg-primary"
        {...fadeUp}
        transition={{ duration: 0.6 }}
      >
        <div className="mx-auto max-w-xl text-center">
          <h2 className="mb-4 font-serif text-3xl font-semibold text-primary-foreground md:text-4xl">
            The people who matter most deserve to feel it.
          </h2>
          <p className="mb-10 text-sm text-primary-foreground/60">
            Start building your relationship memory today. Free, forever.
          </p>
          <motion.div {...buttonHover} className="inline-block">
            <Button
              asChild
              className="rounded-full px-8 py-6 text-base font-medium bg-accent text-accent-foreground hover:bg-accent/90"
              size="lg"
            >
              <Link to="/signup">
                <span className="hidden md:inline">Start for free — show up for the people you love →</span>
                <span className="md:hidden">Start for free →</span>
              </Link>
            </Button>
          </motion.div>
        </div>
      </motion.section>
      </main>

      {/* Footer */}
      <footer className="px-6 py-12 bg-primary">
        <div className="mx-auto max-w-xl text-center">
          <p className="mb-1 font-serif text-xl tracking-widest text-primary-foreground">Circle.</p>
          <p className="mb-4 font-serif text-sm text-primary-foreground/60">Your relationship memory.</p>
          <p className="mb-4 text-sm text-primary-foreground/60">
            Made with care by{" "}
            <a
              href="https://dearestly.co"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-accent"
            >
              Dearestly
            </a>
          </p>
          <div className="mb-4 flex justify-center gap-6 text-sm text-primary-foreground/60">
            <Link to="/privacy" className="hover:text-primary-foreground transition-colors">Privacy Notice</Link>
            <Link to="/terms" className="hover:text-primary-foreground transition-colors">Terms of Use</Link>
          </div>
          <p className="text-xs text-primary-foreground/60">© 2026 Dearestly. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
